/**
 * Limpiadores de los campos del alta de beneficiarios en Venezuela.
 *
 * Reemplazan a los filtros por `keypress`, que se comportaban distinto en cada
 * navegador (Chrome en Android casi no dispara el evento, Safari sí) y que
 * nunca contemplaron el pegado. La idea es la contraria: el campo acepta lo
 * que el cliente escriba o pegue tal como lo copia de su banco
 * («0102-0123-45-1234567890», «V-12.345.678», «+58 0414-123.45.67») y el
 * limpiador lo lleva a lo que se guarda. Nada se recorta: si sobran o faltan
 * dígitos lo dicen los validadores del formulario.
 */

/** Número de cuenta: solo los dígitos, los ceros iniciales son del banco. */
export function limpiarCuentaVe(texto: string): string {
  return (texto || '').replace(/\D/g, '');
}

/**
 * Teléfono de Pago Móvil: los 10 dígitos sin el 0 inicial (4141234567).
 *
 * El 0 de «0414» no se guarda, y el código de país tampoco: se aceptan
 * «+58 414…», «0058 414…» y hasta «+58 0414…», que es como lo escribe mucha
 * gente. Ningún prefijo venezolano empieza por 58, así que quitarlo cuando
 * sobran dígitos no se come un número válido.
 */
export function limpiarTelefonoVe(texto: string): string {
  let digitos = (texto || '').replace(/\D/g, '').replace(/^0+/, '');
  if (digitos.startsWith('58') && digitos.length > 10) {
    digitos = digitos.slice(2).replace(/^0+/, '');
  }
  return digitos;
}

export type LetraDocumentoVe = 'V' | 'E' | 'J';

/**
 * Cédula o RIF: solo los dígitos, y la letra si vino pegada
 * («V-12.345.678», «J-12345678-9»), para que el formulario elija el tipo de
 * documento solo.
 *
 * A la cédula se le quitan los ceros iniciales; al RIF no, porque ahí son
 * parte del número («J-00002542465» es otro RIF que «J-2542465»).
 */
export function limpiarDocumentoVe(
  texto: string,
  esRif = false,
): {
  numero: string;
  letra: LetraDocumentoVe | null;
} {
  const crudo = (texto || '').trim().toUpperCase();
  const coincidencia = /^([VEJ])(?![A-Z])/.exec(crudo);
  const digitos = crudo.replace(/\D/g, '');
  return {
    numero: esRif ? digitos : digitos.replace(/^0+/, ''),
    letra: coincidencia ? (coincidencia[1] as LetraDocumentoVe) : null,
  };
}

/**
 * Nombre, apellido o razón social: en mayúsculas, sin caracteres que el
 * formulario no admite y sin espacios repetidos. El espacio final se deja
 * porque el cliente puede estar por escribir la palabra siguiente.
 */
export function limpiarNombreVe(texto: string, admiteNumeros = false): string {
  const noPermitidos = admiteNumeros
    ? /[^A-ZÁÉÍÓÚÑ0-9\s]/g
    : /[^A-ZÁÉÍÓÚÑ\s]/g;
  return (texto || '')
    .toUpperCase()
    .replace(noPermitidos, '')
    .replace(/\s+/g, ' ')
    .replace(/^ /, '');
}
